import { viewerHeaders } from '../utils/viewerAccess';
import React, { useState, useRef, useEffect } from 'react';
import MeasurementOverlay from '../components/MeasurementOverlay';
import { cloudinaryForDisplay, cloudinaryForVideo } from '../utils/cloudinary';
import { connectLiveViewer } from '../utils/liveViewer';
import AgentBrandCard from '../components/AgentBrandCard';
import WalkthroughMedia from '../components/WalkthroughMedia';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'https://peekr-production.up.railway.app';

export default function ViewerScreen({ content, slides, currentSlide, roomCode, isLiveActive, liveMode, liveImgRef, eventMode, eventName, celebration, guestUploadEnabled, agentBrand, gallery, onGuestUpload, measurements, contentPreview, pdfInfo }) {
  const liveVideoRef = useRef(null);
  const liveRoomRef = useRef(null);                            // LiveKit Room — needed for mic talk-back
  const [liveStatus, setLiveStatus] = useState('connecting'); // connecting|waiting|live|error|disconnected
  const [liveMuted, setLiveMuted] = useState(true);            // muted autoplay → tap to enable sound
  const [liveError, setLiveError] = useState(null);            // exact error text for on-screen diagnosis
  const [micOn, setMicOn] = useState(false);                   // buyer talk-back (mic-only publish)
  const [micErr, setMicErr] = useState(null);

  // LiveKit subscriber — connect only when a NEW (livekit-mode) host goes live.
  // The host publishes camera + mic; we render it listen-only. Old MJPEG hosts
  // use the <img> path in renderLive and never trigger this.
  useEffect(() => {
    if (!isLiveActive || liveMode !== 'livekit' || !roomCode) return;
    let room; let cancelled = false;
    setLiveStatus('connecting');
    setLiveMuted(true);
    setLiveError(null);
    setMicOn(false);
    setMicErr(null);
    connectLiveViewer({
      serverUrl: SERVER_URL,
      roomCode,
      getVideoEl: () => liveVideoRef.current,
      onStatus: (s) => { if (!cancelled) setLiveStatus(s); },
    })
      .then((r) => { if (cancelled) r.disconnect(); else { room = r; liveRoomRef.current = r; } })
      .catch((e) => { console.warn('[live] connect failed:', e); if (!cancelled) { setLiveStatus('error'); setLiveError(e?.message || String(e)); } });
    return () => { cancelled = true; liveRoomRef.current = null; if (room) room.disconnect(); };
  }, [isLiveActive, liveMode, roomCode]);

  // Buyer talk-back — publish the mic into the showing (token allows mic only).
  // The browser asks for permission on first use; echo cancellation is the
  // getUserMedia default, so the agent's voice doesn't loop back.
  const toggleMic = async () => {
    const room = liveRoomRef.current;
    if (!room) return;
    try {
      await room.localParticipant.setMicrophoneEnabled(!micOn);
      setMicOn(!micOn);
      setMicErr(null);
    } catch (e) {
      console.warn('[live] mic toggle failed:', e);
      setMicErr('Mic blocked — allow microphone access in your browser, then tap again.');
    }
  };

  // PDF summary (Wave 3) — generated on demand via the origin-checked proxy.
  const [pdfSummary, setPdfSummary] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState(null);

  const handleSummarizePdf = async () => {
    if (pdfBusy || !pdfInfo) return;
    setPdfBusy(true); setPdfErr(null);
    try {
      const res = await fetch(`${SERVER_URL}/presentation/summarize-pdf-proxy`, {
        method: 'POST',
        headers: await viewerHeaders(roomCode),
        body: JSON.stringify({ pdfUrl: pdfInfo.pdfUrl, title: pdfInfo.pdfTitle, roomCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Summary failed');
      setPdfSummary(data);
    } catch (e) {
      setPdfErr(e.message);
    } finally {
      setPdfBusy(false);
    }
  };

  const renderSingleContent = () => {
    if (!content) return null;
    if (content.type === 'walkthrough') return <WalkthroughMedia content={content} roomCode={roomCode} />;
    if (content.type === 'image') {
      return (
        <div className="content-frame" style={contentPreview ? { backgroundImage: `url("${contentPreview}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
          <img key={content.ts ?? content.data} src={cloudinaryForDisplay(content.data, 2304)} alt="Shared content" />
        </div>
      );
    }
    if (content.type === 'video') {
      return (
        <div className="content-frame">
          <video key={content.ts ?? content.data} src={cloudinaryForVideo(content.data)} controls autoPlay playsInline />
        </div>
      );
    }
    return null;
  };

  // Instant low-res preview shown while the full image uploads (sharpens when
  // content_update arrives). The 64px source scales up blurry — a "loading" look.
  const renderPreview = () => {
    if (!contentPreview) return null;
    return (
      <div className="content-frame">
        <img src={contentPreview} alt="Loading…" style={{ width: '100%', display: 'block' }} />
      </div>
    );
  };

  const renderPresentation = () => {
    if (!slides || slides.length === 0) return null;
    const slide = slides[currentSlide];
    return (
      <div style={{ width: '100%', maxWidth: 480 }}>
        {/* Slide image */}
        <div className="content-frame" style={{ marginBottom: 16 }}>
          <img src={slide} alt={`Slide ${currentSlide + 1}`} />
        </div>

        {/* Slide counter */}
        <div style={{ textAlign: 'center', color: '#6A85A0', fontSize: 13, marginBottom: 12 }}>
          Slide {currentSlide + 1} of {slides.length}
        </div>

        {/* Slide dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
          {slides.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === currentSlide ? 20 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: i === currentSlide ? '#00D4FF' : '#122038',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>

        {/* AI PDF summary — only when the deck came from a PDF (pdfInfo set) */}
        {pdfInfo && (
          <div className="card" style={{ marginTop: 20, padding: '16px' }}>
            {!pdfSummary && !pdfBusy && (
              <button className="btn" onClick={handleSummarizePdf} style={{ width: '100%' }}>
                ✨ Summarize this document
              </button>
            )}
            {pdfBusy && (
              <div style={{ textAlign: 'center', color: '#8A9BB0', fontSize: 13 }}>Reading the document…</div>
            )}
            {pdfErr && (
              <div style={{ textAlign: 'center', color: '#FF6B8A', fontSize: 13 }}>{pdfErr}</div>
            )}
            {pdfSummary && (
              <div style={{ textAlign: 'left' }}>
                <div className="eyebrow" style={{ marginBottom: 8 }}>✨ AI Summary</div>
                <div style={{ fontSize: 14, color: '#C9D6E2', lineHeight: 1.55, marginBottom: 12 }}>
                  {pdfSummary.summary}
                </div>
                {Array.isArray(pdfSummary.takeaways) && pdfSummary.takeaways.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {pdfSummary.takeaways.map((t, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ color: '#00E0FF', fontWeight: 700 }}>›</span>
                        <span style={{ fontSize: 13, color: '#EAF1F8', lineHeight: 1.45 }}>{t}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const isPresentation = slides && slides.length > 0;

  const renderLive = () => (
    // position: relative is required so MeasurementOverlay (position: absolute)
    // is clipped to and sized against this container, not the viewport.
    <div style={{ position: 'relative' }}>
      <div className="content-frame live-frame-wrapper">
        <div className="live-badge">
          <span className="live-dot" />
          LIVE
        </div>
        {liveMode === 'livekit' ? (
          /* WebRTC (LiveKit) — real video + the host's audio. Muted autoplay
             (browsers block autoplay-with-sound); the viewer taps to unmute. */
          <>
            <video
              ref={liveVideoRef}
              className="live-feed-img"
              autoPlay
              playsInline
              muted={liveMuted}
            />
            {liveStatus !== 'live' && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', textAlign: 'center', padding: 20, color: '#8A9BB0', fontSize: 14,
              }}>
                {liveStatus === 'connecting' ? 'Connecting to live video…'
                  : liveStatus === 'waiting' ? "Connected — waiting for the host's camera…"
                  : liveStatus === 'error' ? ('Could not connect' + (liveError ? ': ' + liveError : ' to live video.'))
                  : liveStatus === 'disconnected' ? 'Live ended.'
                  : 'Loading…'}
              </div>
            )}
            {liveStatus === 'live' && liveMuted && (
              <button
                onClick={() => { setLiveMuted(false); if (liveVideoRef.current) { liveVideoRef.current.muted = false; liveVideoRef.current.play?.(); } }}
                style={{
                  position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.7)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 20, padding: '8px 16px', fontSize: 13, cursor: 'pointer', zIndex: 5,
                }}
              >
                🔊  Tap for sound
              </button>
            )}
            {liveStatus === 'live' && (
              <button
                onClick={toggleMic}
                style={{
                  position: 'absolute', bottom: 12, right: 12,
                  background: micOn ? 'rgba(255,61,113,0.92)' : 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: micOn ? '1px solid rgba(255,61,113,0.9)' : '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', zIndex: 5,
                }}
                aria-pressed={micOn}
              >
                {micOn ? '🎤 Mic live — tap to mute' : celebration ? '🎤 Speak to the room' : '🎤 Talk'}
              </button>
            )}
            {micErr && liveStatus === 'live' && (
              <div style={{
                position: 'absolute', bottom: 52, right: 12, maxWidth: 260, zIndex: 5,
                background: 'rgba(7,10,18,0.9)', border: '1px solid rgba(255,61,113,0.5)',
                color: '#FF8FAD', fontSize: 12, borderRadius: 10, padding: '8px 12px', textAlign: 'left',
              }}>
                {micErr}
              </div>
            )}
          </>
        ) : (
          /* Legacy MJPEG — img updated via direct DOM ref, no re-render per frame */
          <img
            ref={liveImgRef}
            className="live-feed-img"
            alt="Live from host"
          />
        )}
      </div>
      {/* Realtor Mode: MeasurementOverlay sits on top of the live image.
          ResizeObserver inside the component tracks the rendered size so
          normalized host coordinates (0–1) remap correctly to viewer pixels. */}
      <MeasurementOverlay measurements={measurements} />
    </div>
  );

  return (
    <div className="screen">
      <div className="room-label" style={{ marginBottom: 20 }}>
        <span className="pulse" />
        {celebration
          ? (isLiveActive ? `🔴 LIVE · ${eventName || roomCode}` : `🎉 ${eventName || roomCode}`)
          : isLiveActive
            ? `🔴 LIVE · SHOWING ${roomCode}`
            : isPresentation
              ? `WALKTHROUGH · SHOWING ${roomCode}`
              : `LIVE SHOWING · ${roomCode}`
        }
      </div>

      {isLiveActive
        ? renderLive()
        : isPresentation
          ? renderPresentation()
          : content
            ? renderSingleContent()
            : renderPreview()
      }

      {agentBrand && (
        <div style={{ marginTop: 22, width: '100%' }}>
          <AgentBrandCard agentBrand={agentBrand} />
        </div>
      )}

      <div className="sub" style={{ marginTop: agentBrand ? 14 : 24 }}>
        {agentBrand
          ? 'powered by Peekr'
          : celebration
            ? (isLiveActive ? "You're right there — via Peekr" : 'Shared via Peekr · getpeekr.com')
            : isLiveActive
              ? 'Live walkthrough from your agent'
              : isPresentation
                ? 'Your agent controls the walkthrough'
                : 'Shared via Peekr · getpeekr.com'
        }
      </div>

      {/* Buyer photo-upload panel retired — uploads removed per rebrand.
          Handlers (handleFileSelect, onGuestUpload) left defined but unmounted. */}
    </div>
  );
}
